<div class="modal-header">
  <div class="head-text">
    <!-- The feature's name, above the question rather than in place of it. "Soft launch
         checklist" was rejected because it reads as something that happens *after* soft launch;
         the question underneath is a different job and keeps the headline, because it names the
         transition the user is actually making. Small and quiet on purpose — it labels the
         modal, it isn't the thing being asked. -->
    <span class="head-eyebrow">Go Live Checklist</span>
    <span class="head-title nav-secondary nav-font18 font-weight-bold">Ready for soft launch?</span>
    @if (!loading) {
      <p class="lede">{{ lede }}</p>
    }
  </div>
  @if (loading || refreshing) {
    <i class="loading ml-3"></i>
  }
  <button type="button" class="head-close" (click)="saving ? null : cancel();" aria-label="Close">
    <i class="far fa-close" aria-hidden="true"></i>
  </button>
</div>

<div class="modal-body">
  @if (loading) {
    <div class="checklist" aria-hidden="true">
      @for (row of [1, 2, 3, 4, 5]; track row) {
        <div class="skeleton-bar">
          <span class="skeleton skeleton-mark"></span>
          <span class="skeleton skeleton-name"></span>
          <span class="skeleton skeleton-state"></span>
        </div>
      }
    </div>
    <span class="sr-only">Loading the go live checklist</span>
  } @else {
    <div class="checklist">
      @for (section of sections; track section.key) {
        <section class="item" [id]="'glc-section-' + section.key"
                 [class.open]="openKey === section.key"
                 [class.done]="confirmed[section.key]"
                 [class.flagged]="section.flagged">

          <!-- Collapsed line: one checkbox, name, current state, chevron. Disabled until
               confirmed, so a section can't be ticked off blind from the collapsed row — the
               panel has to be opened and Yes clicked first. Once confirmed it unlocks, so
               unticking it here is the undo path for a mistaken Yes, without reopening the
               panel; unticking re-locks it, since re-confirming has to go through Yes again. -->
          <div class="bar">
            <div class="or-checkbox bar-cb">
              <input type="checkbox" [id]="'confirm-' + section.key"
                     [checked]="confirmed[section.key]"
                     [disabled]="!confirmed[section.key]"
                     (change)="unconfirmSection(section.key)">
              <label [for]="'confirm-' + section.key"><span class="sr-only">{{ section.question }}</span></label>
            </div>
            <button type="button" class="bar-toggle"
                    [attr.aria-expanded]="openKey === section.key"
                    (click)="toggleOpen(section.key)">
              <span class="bar-name">{{ section.title }}</span>
              <!-- The summary describes the configuration, so it never changes tone when the
                   section is confirmed — the config didn't change, only the sign-off did. A
                   flag is carried by the same amber dot the note below uses, not by recolouring
                   the text red: red read as an error that then stopped being an error the
                   moment it was ticked. Confirmation shows up in the checkbox and the greyed
                   title instead. Never colour alone — a flagged section's words differ too
                   ("None set up" vs "Age, Gender"). -->
              <span class="bar-state">
                @if (section.flagged) {
                  <span class="bar-flag" aria-hidden="true"></span>
                  <span class="sr-only">Needs a look.</span>
                }
                <span class="bar-state-text">{{ section.summary }}</span>
              </span>
              <i class="fas fa-chevron-down bar-chevron" aria-hidden="true"></i>
            </button>
          </div>

          @if (openKey === section.key) {
            <div class="panel">

              <!-- Read-back: every section presents its real current values in the same
                   two-column form, so the eye learns one shape and reuses it. -->
              <div class="panel-data">
                @switch (section.key) {

                  @case ('vendors') {
                    @if (partners.length) {
                      <!-- No "Study goal" row here. It duplicated Specs, and as a lone label/value
                           pair above the table it sat at the opposite end from the allocation
                           total it existed to be compared against. The goal is only worth stating
                           when it's actually being missed, so it moved into the note's own
                           sentence (see allocationLabel), which only renders on a mismatch. -->
                      <table class="vendor-table">
                        <colgroup>
                          <!-- NK-415: the new column is paid for out of Test IDs, the widest
                               column and the only one holding two short words. The vendor name
                               keeps its original 28% — narrowing it was what started truncating
                               names like Borderless Access2. -->
                          <!-- Widths are set so no header wraps: "SL TARGET" and "ALLOCATION"
                               are the long ones at 11px uppercase, and at 12-13% they each fell
                               onto a second line, which is what made the header row twice the
                               height of the rows under it. -->
                          <col style="width: 5%">
                          <col style="width: 24%">
                          <col style="width: 15%">
                          <col style="width: 14%">
                          <col style="width: 15%">
                          <col style="width: 9%">
                          <col style="width: 18%">
                        </colgroup>
                        <thead>
                          <tr>
                            <!-- Back in the leading column, with a real select all in the header.
                                 It was moved out of this position once because a column of ticks
                                 under the section's own confirm checkbox reads as select all -
                                 the answer is to make that true rather than to move the column.
                                 The two are not aligned in any case: the panel indents its
                                 content by 58px, the section checkbox sits at 24px. -->
                            <th scope="col" class="vendor-select">
                              <div class="or-checkbox">
                                <input type="checkbox" id="launch-all"
                                       [checked]="allSelected"
                                       [indeterminate]="someSelected"
                                       (change)="toggleAllSelected()">
                                <label for="launch-all"><span class="sr-only">Soft launch every vendor</span></label>
                              </div>
                            </th>
                            <th scope="col">Vendor</th>
                            <!-- What each vendor is right now. It is the context for the tick
                                 beside it, and it retires the tooltip that used to explain why
                                 an already launched vendor could not be unticked - the row now
                                 says so in the same words the field plan uses.
                                 Headed "Status", the same word the field plan grid uses for the
                                 same value, rather than "Field status". -->
                            <th scope="col">Status</th>
                            <!-- Two different numbers, now side by side so the containment is
                                 visible: "SL target" is the soft launch slice
                                 (partnerSoftQuota), "Allocation" is the vendor's full
                                 contracted share (partnerFullQuota) that the slice comes out
                                 of. Showing only the first meant the note's talk of allocation
                                 referred to a figure the reader had no way to see. -->
                            <th scope="col" class="num">SL target</th>
                            <th scope="col" class="num">Allocation</th>
                            <th scope="col" class="num">CPI</th>
                            <th scope="col">Test IDs</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (p of partners; track p.name) {
                            <tr [class.vendor-held]="!p.selected && !p.alreadyLaunched">
                              <!-- A vendor already past Test keeps a ticked box, disabled: the
                                   tick is true, and disabled says there is nothing here to decide
                                   and nothing will be written either way. No tooltip - the field
                                   status column beside it now says which status that is. -->
                              <td class="vendor-select">
                                <div class="or-checkbox">
                                  <input type="checkbox" [id]="'launch-' + p.partnerId"
                                         [checked]="p.selected || p.alreadyLaunched"
                                         [disabled]="p.alreadyLaunched"
                                         (change)="toggleVendorSelected(p)">
                                  <label [for]="'launch-' + p.partnerId"><span class="sr-only">{{ p.alreadyLaunched ? p.name + ' is already in soft launch' : 'Soft launch ' + p.name }}</span></label>
                                </div>
                              </td>
                              <td class="vendor-name">{{ p.name }}</td>
                              <td class="field-status">
                                @if (p.fieldStatus) {
                                  <span class="nav-status {{ p.fieldStatusClass }}">{{ p.fieldStatus }}</span>
                                } @else {
                                  <span class="kv-empty">Not set</span>
                                }
                              </td>
                              <td class="num tnum">{{ p.softQuota ?? 0 }}</td>
                              <td class="num tnum">{{ (p.fullQuota ?? 0) | number }}</td>
                              <td class="num tnum">{{ (p.cpi ?? 0) | currency:'USD':'symbol':'1.2-2' }}</td>
                              <td>
                                @if (p.testIdsReturned) {
                                  <span class="status-ok"><i class="fas fa-check" aria-hidden="true"></i> Returned</span>
                                } @else {
                                  <span class="status-none"><i class="fa fa-xmark" aria-hidden="true"></i> Not tested</span>
                                }
                              </td>
                            </tr>
                          }
                        </tbody>
                        <!-- Totals, so the reader can check the allocation sum against the study
                             goal above without doing the addition themselves. CPI is left blank
                             on purpose: a column of CPIs has no meaningful total, and an
                             unweighted average would be a number that looks authoritative and
                             isn't. -->
                        <tfoot>
                          <tr>
                            <!-- The label states the scope, so the totals row answers "what am I
                                 launching with" rather than "what is set up". The totals
                                 themselves stay single figures: a held number bolted onto each
                                 one turned a row of two clean values into four, wrapped onto two
                                 lines, and read as arithmetic nobody asked for. What is held is
                                 said in words in the note instead. -->
                            <td class="total-label" colspan="2">{{ vendorScopeLabel }}</td>
                            <td></td>
                            <td class="num tnum">{{ totalSoftLaunchTarget | number }}</td>
                            <td class="num tnum">{{ totalVendorAllocation | number }}</td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>

                    }
                  }

                  @case ('quotas') {
                    @if (hasQuotas) {
                      <dl class="kv">
                        <dt>Groups</dt><dd>{{ quotaGroupNames.join(', ') }}</dd>
                      </dl>
                    }
                  }

                  <!-- Always rendered, with "Not set" per row, matching how Specs already
                       handles a blank. The whole list used to be wrapped in an @if requiring
                       both dates, so a project missing either one showed an empty panel — the
                       section opened and appeared to contain nothing at all. A missing date is
                       a fact worth seeing in the same place as the ones that are set. -->
                  @case ('dates') {
                    <dl class="kv">
                      <dt>Starts</dt>
                      <dd [class.kv-empty]="!fieldStartDate">
                        @if (fieldStartDate) {
                          {{ fieldStartDate | date:'MMM d, y' }}
                        } @else {
                          Not set
                        }
                      </dd>
                      <dt>Ends</dt>
                      <dd [class.kv-empty]="!fieldEndDate">{{ fieldEndDate ? (fieldEndDate | date:'MMM d, y') : 'Not set' }}</dd>
                      @if (fieldStartDate && fieldEndDate) {
                        <dt>In field</dt><dd class="tnum">{{ daysInField }} days</dd>
                      }
                    </dl>
                  }

                  @case ('specs') {
                    <dl class="kv">
                      <dt>Completes</dt>
                      <dd [class.kv-empty]="!(completesValue > 0)" class="tnum">{{ completesValue > 0 ? completesValue : 'Not set' }}</dd>
                      <dt>Incidence</dt>
                      <dd [class.kv-empty]="!(projectIR > 0)" class="tnum">{{ projectIR > 0 ? projectIR + '%' : 'Not set' }}</dd>
                      <dt>LOI</dt>
                      <dd [class.kv-empty]="!(projectLOI > 0)" class="tnum">{{ projectLOI > 0 ? projectLOI + ' min' : 'Not set' }}</dd>
                    </dl>
                  }

                  @case ('quality') {
                    <dl class="kv">
                      <dt>Duplication check</dt><dd>{{ duplicationLabel }}</dd>
                      <!-- The statuses and the project list only describe where de-duplication
                           applies, so with the master switch off they'd read as protection that
                           isn't running. -->
                      @if (project?.isDeDupeDeviceEnabled) {
                        <dt>Applies to</dt>
                        <dd [class.kv-empty]="!dedupeStatuses.length">
                          {{ dedupeStatuses.length ? dedupeStatuses.join(', ') : 'No statuses selected' }}
                        </dd>
                        <dt>De-duped vs</dt>
                        <dd>
                          @if (dedupeProjectNames.length) {
                            {{ dedupeProjectNames.length }} other project{{ dedupeProjectNames.length === 1 ? '' : 's' }}
                            <button type="button" class="kv-toggle" (click)="showDedupeList = !showDedupeList"
                                    [attr.aria-expanded]="showDedupeList">{{ showDedupeList ? 'hide' : 'show' }}</button>
                            @if (showDedupeList) {
                              <ul class="dedupe-list">
                                @for (name of dedupeProjectNames; track $index) {
                                  <li>{{ name }}</li>
                                }
                              </ul>
                            }
                          } @else {
                            This project only
                          }
                        </dd>
                      }
                      <dt>CleanID</dt><dd>{{ cleanIdLabel }}</dd>
                      <dt>Mobile</dt><dd>{{ project?.allowMobile ? 'Allowed' : 'Not allowed' }}</dd>
                      <dt>Link security</dt><dd>{{ linkSecurityOn ? 'On' : 'Off' }}</dd>
                    </dl>
                  }
                }
              </div>

              <!-- The note is a plain tinted line, not a bordered card - matching the mockup,
                   which never puts a ring around this. One fact per line: joining them into a
                   single paragraph produced a run-on that read as one confused sentence rather
                   than two clear ones. -->
              <!-- The box carries no link of its own. It used to end its last line with a fix
                   link, so where the way out lived depended on whether NAV had found anything -
                   inside the note when it had, under the question when it hadn't. Two placements
                   for one destination meant the reader had to look for it. The findings are now
                   purely what NAV observed, and the single way out is anchored under the
                   question on every section (see .ask-no below). -->
              <!-- NK-315 AC3: blockers sit in the same box as the notes, below them and after a
                   blank row, in red. Same box because they're the same kind of thing to the
                   reader - what NAV found - and splitting them into two panels would put two
                   competing alerts in one panel. Separated and recoloured because they're not
                   the same kind of thing to act on: a note is weighed and confirmed anyway, a
                   blocker has to be fixed elsewhere before this section can be answered at all.
                   Last in the box so the red line sits directly above the Yes button it
                   disables, which is the adjacency that explains the dead control. -->
              <!-- NK-415: the scope line is the box's first bullet. It is the frame the rest are
                   read against - a soft launch target of 0 against a vendor only means something
                   once it is known that vendor is in the launch - so it leads rather than sits
                   underneath them. It is a bullet like the others and not a set-apart header:
                   undotted and divided off it read as two stacked panels, and left whatever
                   single bullet followed it looking orphaned. -->
              @if (hasFindings(section.key)) {
                <div class="note">
                  @if (sectionLead(section.key)) {
                    <span class="note-line">{{ sectionLead(section.key) }}</span>
                  }
                  @for (line of sectionNotes(section.key); track $index) {
                    <span class="note-line">{{ line }}</span>
                  }
                  @if (sectionBlockers(section.key).length) {
                    <div class="note-blockers" [id]="'glc-blocked-' + section.key">
                      @for (line of sectionBlockers(section.key); track $index) {
                        <span class="note-line note-line-blocking">{{ line }}</span>
                      }
                    </div>
                  }
                </div>
              }

              <!-- The ask: the question on the left, a real Yes button on the right — the
                   same control this whole modal is built around, reused from the footer's own
                   primary button so "commit to this" always looks the same. This is the only
                   way to confirm a section the first time; the bar's checkbox mirrors the
                   result and, once confirmed, doubles as the undo control. -->
              <div class="ask">
                <span class="ask-q" [id]="'ask-q-' + section.key">{{ section.question }}</span>
                <!-- Disabled, not hidden: the question still has to be visible for the panel to
                     make sense, and the button greying out next to the red line above it is what
                     says "not yet" rather than "not here". aria-describedby points at the blocker
                     list while blocked, so the reason is read out with the control. -->
                <button type="button" class="btn nav-btn-accent btn-sm shadow-none"
                        [disabled]="section.blocked"
                        [attr.aria-describedby]="section.blocked ? 'glc-blocked-' + section.key : 'ask-q-' + section.key"
                        (click)="confirmSection(section.key)">Yes</button>
              </div>

              <!-- The other answer, on every section - always rendered, always here, whether or
                   not NAV found anything. Consistency is the whole point: the reader learns one
                   place to look for "this isn't right, take me to it" and finds it in that place
                   every time, instead of hunting for a link that moves depending on the section's
                   state. A flag says what NAV saw; this says what to do about it.
                   Deliberately NOT in a tinted note box: the amber fill means "NAV found
                   something", and this is an answer to the question, not a finding - borrowing
                   the fill would read as a second alarm. It sits directly under the question it
                   answers, where a floating link under the values read as unattached. A text
                   link against a solid button keeps the hierarchy honest: Yes is the act, this
                   is the way out. -->
              <div class="ask-no">
                <button type="button" class="note-link" (click)="openFixPage(section.linkSection)">No - {{ section.noLabel }}<i
                  class="fa-light fa-square-arrow-right" aria-hidden="true"></i> <span
                  class="link-hint">(opens in a new browser tab)</span></button>
              </div>

            </div>
          }
        </section>
      }
    </div>
  }
</div>

<div class="modal-footer">
  @if (!loading) {
    <span class="progress-text" [class.complete]="allConfirmed">
      @if (allConfirmed) {
        All confirmed
      } @else {
        @if (confirmableRemainingCount) {
          <span class="tnum">{{ confirmableRemainingCount }}</span> to confirm
        }
        @if (errorCount) {
          @if (confirmableRemainingCount) { <span class="progress-sep" aria-hidden="true">&middot;</span> }
          <span class="progress-blocked"><span class="tnum">{{ errorCount }}</span> to fix</span>
        }
      }
    </span>
    <div class="footer-actions">
      <button type="button" class="btn nav-btn-primary btn-sm shadow-none" (click)="saving ? null : cancel();">Cancel</button>
      @if (saving) {
        <span class="loading"></span>
      }
      <button type="button" class="btn nav-btn-accent btn-sm shadow-none"
              [class.disabled]="!allConfirmed" (click)="confirmAndLaunch()">
        Confirm &amp; Launch
      </button>
    </div>
  }
</div>
